import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExerciseTitleBar } from '../../ExerciseTitleBar';
import { Answers } from './answers';
import { Dialog, useMediaQuery, useTheme } from '@material-ui/core';
import { ConfirmationDialog } from '../../../../shared/components';
import { UpdateExerciseForm } from '../ExerciseForm';
import { Exercise, ExerciseType } from '../../../../../config/api/models/Exercise';
import { GroupStudentSeriesScores } from '../../../../../config/api/models/Score';
import { BlockWithNotes } from '../../../../../config/api/models/Block';
import {
  TrajectorySectionOrWorksheetType,
  TrajectoryViewMode,
} from '../../../../../config/api/models/Trajectory';
import { AccordionDetails } from './content';
import { PrintMessage, StyledAccordion, StyledAccordionSummary } from './Atoms';
import { getAverageExerciseScoreForGroup } from '../../../helpers';
import { useParams } from 'react-router';
import { UrlParams } from '../../../../shared/types';
import { FeedbackModule } from '../../../../feedback';
import { Solutions } from './solution';
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/useAppDispatch';
import { deleteBlock, addBlock, addBlockType } from '../../../../shared/slices/blocks/api/blocks';
import { setExerciseToDuplicate } from '../../../../shared/slices/blocks/entity/BlocksSlice';
import { ToolboxPanelModeType } from 'src/modules/shared/slices/trajectoryToolbox/trajectoryToolbox';
import {
  useGetGroupPageAnswersQuery,
  useGetUserPageAnswersQuery,
} from 'src/modules/shared/slices/exercises/api/exercise';
import { useOnScreen, usePageSolutions } from 'src/modules/trajectory/hooks';
import { StudentFiltersEnum } from 'src/modules/shared/constants';
import WebSocketConnector, { Connector } from 'src/signalr-connection';
import {
  BackendUserBlockAnswers,
  UserBlockAnswers,
  mapBackendUserBlockAnswersToUserBlockAnswers,
} from 'src/config/api/models/Answers';
import { useGetStudentBookmarkQuery } from 'src/modules/shared/slices/bookmark/api/bookmarkApi';
import { useUser } from 'src/modules/shared';
import { ExerciseFormInitialValues } from 'src/modules/trajectory/types';

type Props = {
  index?: number;
  expanded: string | false;
  onChangeExpanded?: (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  seriesId: string;
  pageType: TrajectorySectionOrWorksheetType;
  exercise: Exercise | BlockWithNotes;
  mode: TrajectoryViewMode;
  teacherProgressAndScores?: GroupStudentSeriesScores;
  onUpdateExercise?: () => void;
  hideDeleteButton?: boolean;
  hideTitlePrefix?: boolean;
  lazyLoad?: boolean;
  feedbackEnabled: boolean;
  ref?: Element | undefined;
};

const getExerciseLevel = (level: string | undefined | number) => {
  if (!level || level === undefined) return '1';
  if (level === 'Easy' || level === '1' || level === 1) return '1';
  if (level === 'Average' || level === '2' || level === 2) return '2';
  if (level === 'Hard' || level === '3' || level === 3) return '3';
  return '1';
};

const ExerciseDetailContent: FC<Props> = ({
  index,
  expanded,
  onChangeExpanded,
  seriesId,
  pageType,
  exercise,
  mode,
  onUpdateExercise,
  teacherProgressAndScores,
  hideDeleteButton = false,
  hideTitlePrefix = false,
  lazyLoad = false,
  feedbackEnabled,
}) => {
  // hooks
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { user, isStudent } = useUser();
  const { t } = useTranslation();
  const { bookId } = useParams<UrlParams>();
  const bookmarkMode = useAppSelector((state) => state.bookmark.bookmarkMode);
  const { data: studentBookmarksData } = useGetStudentBookmarkQuery(bookId as string, {
    skip: !isStudent,
  });
  const bookmark = useMemo(() => {
    if (studentBookmarksData === undefined) return undefined;
    return studentBookmarksData;
  }, [studentBookmarksData]);

  const [connection, setConnection] = useState<Connector | undefined>();

  // redux
  const solutionsMode = useAppSelector((state) => state.solutionsMode.solutionsMode);
  const selectedStudent = useAppSelector((state) => state.filters.selectedStudent);
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const refresh = useAppSelector((state) => state.filters.refresh);
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const toolboxPanelMode = useAppSelector((state) => state.trajectoryToolbox.toolboxPanelMode);
  const jwt = useAppSelector((state) => state.auth.jwt);
  const blocks = useAppSelector((state) => state.blocks.blocks);

  // state
  const [openConfirmDialogDelete, setOpenConfirmDialogDelete] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [userAnswers, setUserAnswers] = useState<UserBlockAnswers | undefined>();

  // mediaquery
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const ref = useRef<HTMLElement>(null);
  const isVisible = useOnScreen(ref);
  const {
    data: userPageAnswers,
    refetch: userRefetchAnswers,
    isUninitialized: userRequestIsUninitialized,
  } = useGetUserPageAnswersQuery(
    { userId: isStudent ? user.id : selectedStudent!, blockId: exercise.id },
    {
      skip: (!isVisible || !selectedStudent) && !isStudent,
    }
  );

  useEffect(() => {
    if (!isStudent) return;

    setConnection(WebSocketConnector('application', jwt as string));
  }, [isStudent, jwt]);

  useEffect(() => {
    setUserAnswers(userPageAnswers);
  }, [userPageAnswers]);

  useEffect(() => {
    if (!isStudent) return;

    connection?.listenToEvent<BackendUserBlockAnswers>('NewScoreMessage', (userExerciseAnswers) => {
      const mapperAnswers = mapBackendUserBlockAnswersToUserBlockAnswers(userExerciseAnswers);
      if (exercise?.url === mapperAnswers?.exerciseUrl) {
        setUserAnswers(mapperAnswers);
      }
    });

    return () => {
      connection?.stopListeningToEvent('NewScoreMessage');
    };
  }, [connection, exercise?.url, isStudent]);

  useEffect(() => {
    if (
      (refresh || activeFilter === StudentFiltersEnum.PROGRESS_AND_SCORES) &&
      !userRequestIsUninitialized
    ) {
      userRefetchAnswers();
    }
  }, [refresh, userRequestIsUninitialized, userRefetchAnswers, activeFilter]);

  const {
    data: groupPageAnswers,
    refetch: groupRefetchAnswers,
    isUninitialized: groupRequestIsUninitialized,
  } = useGetGroupPageAnswersQuery(
    { groupId: selectedGroup!, blockId: exercise.id },
    {
      skip: !isVisible || !selectedGroup,
    }
  );

  useEffect(() => {
    if (refresh && !groupRequestIsUninitialized) {
      groupRefetchAnswers();
    }
  }, [exercise.id, expanded, groupRefetchAnswers, groupRequestIsUninitialized, refresh]);

  const handleUpdateExercise = useCallback(() => {
    setDialogOpen(false);

    if (onUpdateExercise !== undefined) onUpdateExercise();
  }, [onUpdateExercise]);

  const handleDeleteExerciseDialogClose = useCallback(
    (result: boolean) => {
      if (result) {
        dispatch(deleteBlock({ bookId: bookId!, pageId: seriesId, blockId: exercise.id }));
      }
      setOpenConfirmDialogDelete(false);
    },
    [dispatch, seriesId, exercise, bookId]
  );

  const averageExerciseScoreForGroup: string = !!teacherProgressAndScores
    ? getAverageExerciseScoreForGroup(teacherProgressAndScores, seriesId, exercise.id)
    : '';

  const handleDuplicateExerciseClick = useCallback(() => {
    let payload: addBlockType['payload'] = {
      title:
        exercise.title != null && exercise.title.replace(' ', '') !== ''
          ? `${exercise.title} - ${t('shared.duplicatePostFix')}`
          : undefined,
      level: exercise.level?.toString(),
      bloom_type: exercise.bloom_type,
      type: exercise.type,
      previous_block_id: exercise.id,
    };

    const cluster = blocks?.find((item) => item.id === exercise.parent_id);

    if (cluster?.sections) {
      cluster?.sections.map((item) => {
        return item.childBlocks?.map((childItem) => {
          if (childItem.id === exercise.id) {
            payload = {
              title:
                childItem.title != null && childItem.title.replace(' ', '') !== ''
                  ? `${childItem.title} - ${t('shared.duplicatePostFix')}`
                  : undefined,
              level: childItem.level?.toString(),
              bloom_type: childItem.bloom_type,
              type: childItem.type,
              previous_block_id: exercise.id,
              cluster_id: childItem.parent_id,
              section_id: item.id,
            };
            return childItem;
          }
          return childItem;
        });
      });
    }

    dispatch(
      addBlock({
        bookId: bookId!,
        pageId: seriesId,
        payload,
        isExercisePage: index !== undefined,
      })
    );

    dispatch(setExerciseToDuplicate({ originalUrl: exercise.url as string }));
  }, [
    exercise.title,
    exercise.level,
    exercise.bloom_type,
    exercise.type,
    exercise.id,
    exercise.url,
    exercise.parent_id,
    t,
    blocks,
    dispatch,
    bookId,
    seriesId,
    index,
  ]);

  const handleToggleFeedback = useCallback(
    (event: React.MouseEvent<HTMLButtonElement> | React.ChangeEvent<{}>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!expanded && onChangeExpanded) {
        onChangeExpanded(`exercise_${exercise.id}`)(event, true);
      }
      setShowFeedback((prev) => !prev);
    },
    [expanded, onChangeExpanded, exercise]
  );

  const handleChangeExpanded = useCallback(
    (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      if (!isExpanded) setShowFeedback(false);

      return !!onChangeExpanded
        ? onChangeExpanded(`exercise_${exercise.id}`)(event, isExpanded)
        : undefined;
    },
    [exercise, onChangeExpanded]
  );

  const updateFormInitialValues: ExerciseFormInitialValues = {
    title: exercise.title || '',
    description: exercise.description,
    level: getExerciseLevel(exercise.level),
    bloom_type: exercise.bloom_type || '',
    type: exercise.type,
    algebra_kit_exercise_id: exercise.algebra_kit_exercise_id,
    exercise_tool: exercise.exercise_tool || '1',
  };

  const solutions = usePageSolutions(seriesId, pageType, solutionsMode);
  const blocklSolution = exercise.url ? solutions.solutions?.[exercise.url] : undefined;

  return (
    <>
      <StyledAccordion
        mode={mode}
        expanded={expanded === `exercise_${exercise.id}` || expanded === `all`}
        onChange={handleChangeExpanded}
        isMobile={isMobile}
        bookmarkMode={bookmarkMode}
        isBookmarked={
          bookmark?.length && bookmark.filter((item) => item.blockId === exercise.id).length
        }
        elevation={0}
        hasNote={exercise.note || toolboxPanelMode === ToolboxPanelModeType.NOTES}
        type={exercise.type}
      >
        <StyledAccordionSummary ref={ref as any} className={`${mode}`}>
          <ExerciseTitleBar
            exercise={exercise}
            seriesId={seriesId}
            userAnswersFromPage={userAnswers}
            groupAnswersFromPage={groupPageAnswers}
            teacherProgressAndScores={teacherProgressAndScores}
            averageExerciseScoreForGroup={averageExerciseScoreForGroup}
            mode={mode}
            index={index}
            onDeleteExercise={() => setOpenConfirmDialogDelete(true)}
            onUpdateExercise={() => setDialogOpen(true)}
            onDuplicateExercise={handleDuplicateExerciseClick}
            hideDeleteButton={hideDeleteButton}
            hideTitlePrefix={hideTitlePrefix}
            feedbackEnabled={feedbackEnabled}
            onToggleFeedback={handleToggleFeedback}
            expanded={expanded}
          />
        </StyledAccordionSummary>
        {showFeedback && (
          <FeedbackModule pageId={seriesId} blockId={exercise.id} context="block" mode="embedded" />
        )}
        {(expanded === `exercise_${exercise.id}` || expanded === 'all') && (
          <>
            {solutionsMode &&
              selectedStudent === '' &&
              exercise.type !== ExerciseType.ALGEBRA_KIT &&
              exercise.type !== ExerciseType.TEXT_EXERCISE && (
                <Solutions solution={blocklSolution} />
              )}
            <Answers
              exercise={exercise as Exercise}
              groupAnswersFromPage={groupPageAnswers}
              userAnswersFromPage={userAnswers}
              solution={blocklSolution}
            />
          </>
        )}
        <AccordionDetails
          exercise={exercise as Exercise}
          userAnswersFromPage={userAnswers}
          expanded={expanded}
          lazyLoad={lazyLoad}
        />
        <ConfirmationDialog
          open={openConfirmDialogDelete}
          title={t('trajectory.sections.exercises.confirmDeleteExercise.title')}
          description={t('trajectory.sections.exercises.confirmDeleteExercise.description')}
          onClose={handleDeleteExerciseDialogClose}
        />
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          aria-labelledby="add-iframe-form"
          fullWidth
        >
          <UpdateExerciseForm
            seriesId={seriesId}
            exerciseId={exercise.id}
            bookId={bookId as string}
            onUpdateExercise={handleUpdateExercise}
            onCancel={() => setDialogOpen(false)}
            mode="dialog"
            type="update"
            initialValues={updateFormInitialValues}
          />
        </Dialog>
      </StyledAccordion>
      <PrintMessage>{'(' + t('trajectory.sections.shared.video.print') + ')'}</PrintMessage>
    </>
  );
};

export default ExerciseDetailContent;
